import {
  AlertColor,
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableContainer,
  Typography,
  autocompleteClasses,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import ProgramPanel from '../../components/forms/ProgramPanel';
import {
  DayCareQuestions,
  EvaluationQuestions,
  LEAQuestion,
  LEAReferralType,
  headCells,
} from './type';
import MediumTypography from '../../components/formlib/MediumTypography';
import SingleLine from '../../components/formlib/SingleLine';
import ConsentGeneralInfo from '../../pages/consent/ConsentGeneralInfo';
import CustomRadioButton from '../../components/formlib/CustomRadioButton';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import Textinput from '../../components/formlib/Textinput';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import { useIntl } from 'react-intl';

import dayjs, { Dayjs } from 'dayjs';
import {
  formatPhoneNumber,
  nameValidation,
} from '../../utils/formValidationUtil';
import {
  downloadLeaPreSchool,
  getLEAPreschoolInfo,
  updateLEAPreschoolInfo,
} from '../../services/referralClients/leaPreSchoolService';
import {
  ConsentClientContact,
  ConsentClientSignature,
} from '../../services/configApi/forms/Consent/ConsentServices';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { TableHeader } from '../../components/formlib/TableHeader';
import ModalPopup from '../../components/formlib/ModalPopup';
import LanguageSelectionDialog from '../../components/shared/LanguageSelectionDialog';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { OptionType } from '../../utils/type';
import AppTimePicker from '../../components/formlib/AppTimePicker';
import {
  datePickerMinDateMaxDateValidate,
  getTime,
} from '../../utils/dateUtil';
import moment from 'moment';
import { debounce } from 'lodash';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import { formatName } from '../../utils/nameUtils';
import { getClientDocumentFileName } from '../../utils/fileUtils';
import { checkOffline } from '../../services/Offline';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { LEA_REFERRAL_DASHBOARD } from '../../routes/Routing';
import {
  REFERRAL_STATUS_KEY,
  DATE_FORMAT,
  LOCAL_DATE_TIME_UTC_FORMAT,
} from '../../services/Constant';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import ConsentSignature from '../../pages/consent/ConsentSignature';
import ConsentGRI from '../../pages/consent/ConsentJson/Consent-GRI.json';
import MultipleAutocompleteSearch from '../../components/formlib/MultipleAutocompleteSearch';
import { Parent } from '../eligibility/types';
import SignatureName from '../../components/formlib/modal/SignatureName';
import { ReactComponent as Editsignature } from '../../assets/images/editsignature.svg';
import SignatureComponent from '../../components/formlib/modal/SignatureComponent';
import ServiceDeliveryPlanTableRow from '../ifspNew/ifspSDPAndConsentScreens/sDPAndConsentsComponents/ServiceDeliveryPlanTableRow';
import { ServicePlanType } from '../../redux/features/ifsp/ifspSDPAndConsentSlice';

const interPreterOptions: OptionType[] = [
  { id: 'Child', label: 'Child' },
  { id: 'Parent/Guardian', label: 'Parent/Guardian' },
];
const validationSchema = yup.object().shape({
  referralDate: yup
    .date()
    .nullable()
    .required('AddNewReferralDetails.referralDate.Validation'),
  daysThirdBirth: yup.number().nullable(),
  referringTo: yup.string().nullable().required('referringToText.Validation'),
  lastEvaluationDate: yup
    .date()
    .when('evaluationFrom', {
      is: 'Other Assessment',
      then: yup.date().required('lastEvaluationDateText.Validation').nullable(),
    })
    .nullable(),
  attachBDI2Results: yup.bool().nullable(),
  attachOther: yup.bool().nullable(),
  attachIFSP: yup.bool().nullable(),
  childIsInDayCare: yup.boolean().nullable(),
  dayCareName: yup
    .string()
    .when('childIsInDayCare', {
      is: true,
      then: yup.string().required('dayCareNameText.Validation'),
    })
    .nullable(),
  dayCarePhoneNumber: yup
    .string()
    .when('childIsInDayCare', {
      is: true,
      then: yup
        .string()
        .min(12, 'PhoneNumber.Validation')
        .required('dayCarePhoneText.Validation'),
    })
    .nullable(),
  childSchedule: yup
    .string()
    .when('childIsInDayCare', {
      is: true,
      then: yup.string().required('childScheduleText.Validation'),
    })
    .nullable(),
  dayCareAddress: yup
    .string()
    .when('childIsInDayCare', {
      is: true,
      then: yup.string().required('addressRequiredValidation'),
    })
    .nullable(),
  otherReleventInfo: yup.string().required('otherReleventInfoText.Validation'),
  otherText: yup
    .string()
    .when('other', {
      is: true,
      then: yup.string().required('otherText.Validation'),
    })
    .nullable(),
  areasOfConcern: yup
    .object()
    .shape({
      adaptive: yup.string().nullable(),
      communication: yup.string().nullable(),
      socialEmotional: yup.string().nullable(),
      motor: yup.string().nullable(),
      cognitive: yup.string().nullable(),
      vision: yup.string().nullable(),
      hearing: yup.string().nullable(),
      otherConcern: yup.string().nullable(),
    })
    .nullable(),
  tpc: yup
    .object()
    .shape({
      date: yup.string().nullable(),
      time: yup
        .string()
        .when('date', (date: string, schema) => {
          if (date && date !== null) {
            return schema.required('timeRequiredValidation');
          }
          return schema;
        })
        .nullable(),
      location: yup.string().nullable(),
    })
    .nullable(),
  questionnaire: yup
    .array()
    .of(
      yup.object().shape({
        question: yup.string(),
        answer: yup.string().when('question', (question: string, schema) => {
          if (question && question.length > 0) {
            return schema.required('answerRequiredValidation');
          }
          return schema;
        }),
      }),
    )
    .nullable(),
  employeeSignature: yup
    .object()
    .shape({
      signature: yup.string(),
      partitionkey: yup.string(),
      rowkey: yup.string(),
      name: yup.string().nullable(),
      date: yup.string().nullable(),
    })
    .nullable(),
  signature: yup
    .object()
    .shape({
      signature: yup.string(),
      partitionKey: yup.string(),
      rowKey: yup.string().nullable(),
      name: yup.string().nullable(),
      date: yup.string().nullable(),
    })
    .nullable(),
  interpreterNeeded: yup.string().nullable(),
  birthPlaceCity: yup.string().nullable(),
  birthPlaceState: yup.string().nullable(),
  childResidesWith: yup.string().nullable(),
});

const validateForm = (value: LEAReferralType) => {
  const errors: Partial<LEAReferralType> = {};

  if (datePickerMinDateMaxDateValidate(value.referralDate)) {
    errors.referralDate = 'datePickerMinDateMaxDateValidate';
  }
  if (datePickerMinDateMaxDateValidate(value.lastEvaluationDate)) {
    errors.lastEvaluationDate = 'datePickerMinDateMaxDateValidate';
  }
  if (datePickerMinDateMaxDateValidate(value?.tpc?.date ?? '')) {
    errors.tpc = {
      date: 'datePickerMinDateMaxDateValidate',
      location: errors.tpc?.location ?? '',
      time: errors.tpc?.time ?? '',
    };
  }
  return errors;
};

const firstValues: LEAReferralType = {
  type: '',
  otherReleventInfo: '',
  requestedDate: '',
  referralDate: '',
  dischargeReason: '',
  daysThirdBirth: null,
  referralReason: '',
  referralReasonOther: '',
  referringTo: '',
  currentIssues: '',
  currentOtherIssues: '',
  lastEvaluationDate: '',
  evaluationFrom: '',
  attachBDI2Results: false,
  other: false,
  otherText: '',
  attachIFSP: false,
  childIsInDayCare: false,
  dayCareName: '',
  dayCarePhoneNumber: '',
  dayCareAddress: '',
  childSchedule: '',
  consentId: '',
  leaConsentSigned: false,
  earlyInterventionService: [],
  areasOfConcern: {
    adaptive: '',
    communication: '',
    socialEmotional: '',
    motor: '',
    cognitive: '',
    vision: '',
    hearing: '',
    otherConcern: '',
    behavior: '',
  },
  questionnaire: [] as LEAQuestion[],
  employeeSignature: {
    rowkey: '',
    partitionkey: '',
    signatureValue: '',
    name: '',
    date: '',
  },
  parentSignature: {
    rowkey: '',
    partitionkey: '',
    signatureValue: '',
    name: '',
    date: '',
  },
  selectedContact: [],
  contact: [
    {
      id: '',
      name: '',
      address: '',
      email: '',
      phone: '',
      isGuardian: false,
      isPrimary: false,
      isLegalGuardian: false,
      type: '',
    },
  ],
  signedDate: '',
  signature: [
    {
      id: '',
      name: '',
      relationship: '',
      email: '',
      signature: '',
      verbal: false,
      date: '',
      paperConsent: false,
      license: '',
      rowKey: '',
      partitionKey: '',
      isPrimary: false,
      isLegal: false,
      isGuardian: false,
    },
  ],

  tpc: {
    date: '',
    time: '',
    location: '',
  },
  createdOn: '',
  modifiedOn: '',
  createdBy: '',
  modifiedBy: '',
  authorizationStatus: '',
  revokedDate: '',
  revokeSignature: null,
  refusal: false,
  interpreterNeeded: '',
  birthPlaceCity: '',
  birthPlaceState: '',
  childResidesWith: '',
  requester: {
    name: '',
    email: '',
    phone: '',
  },
  uiVersion: 1,
  parent: {
    address: '',
    contactId: '',
    email: '',
    homePhoneNumber: '',
    isPrimary: true,
    name: '',
    relationshipToClient: '',
    type: '',
  },
};

interface UnsavedChangesWatcherProps {
  values: LEAReferralType;
  initialValues: LEAReferralType | undefined;
  currentValues: LEAReferralType | undefined;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
}

const UnsavedChangesWatcher = ({
  values,
  initialValues,
  currentValues,
  onUnsavedChangesChange,
}: UnsavedChangesWatcherProps) => {
  useEffect(() => {
    if (JSON.stringify(initialValues) !== JSON.stringify(currentValues)) {
      onUnsavedChangesChange(true);
    } else {
      onUnsavedChangesChange(false);
    }
  }, [values]);

  return null;
};

const LEAPreschoolForm = () => {
  const navigate = useNavigate();
  const rctl = useIntl();
  const [eiService, setEiService] = useState<OptionType[]>([]);
  const [locationOfService, setLocationOfService] = useState<OptionType[]>([]);
  const [period, setPeriod] = useState<OptionType[]>([]);
  const [intensity, setIntensity] = useState<OptionType[]>([]);
  const [providedBy, setProvidedBy] = useState<OptionType[]>([]);
  const [lookUpReasons, setLookUpReasons] = useState<OptionType[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const formRef = useRef<FormikProps<LEAReferralType>>(null);
  const [leaReferral, setLeaReferral] = useState<LEAReferralType>(firstValues);
  const [leaReferralBackup, setLeaReferralBackup] =
    useState<LEAReferralType>(firstValues);
  const [toastrId, setToastrId] = useState<string>();

  const [parentSignatureClicked, setParentSignatureClicked] =
    useState<boolean>(false);
  const [employeeSignatureClicked, setEmployeeSignatureClicked] =
    useState<boolean>(false);
  const [openDownloadLanguageModal, setOpenDownloadLanguageModal] =
    useState<boolean>(false);

  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { lookups } = useContext(LookUpContext);
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const [referralReasonsSelected, setReferralReasonsSelected] = useState<
    string[]
  >([]);
  const [
    initialReferralReasonsSelectedValue,
    setInitialReferralReasonsSelectedValue,
  ] = useState<string[]>([]);
  const [lastUpdatedBy, setLastUpdatedBy] = useState<string>('');
  const parentRef = useRef<HTMLDivElement>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [enableDownload, setEnableDownload] = useState(false);

  const [guardianContacts, setGuardianContacts] = useState<OptionType[]>([]);
  const [guardianContactsArray, setGuardianContactsArray] = useState<Parent[]>(
    [],
  );
  const [contactSelected, setContactSelected] = useState<OptionType | null>(
    null,
  );
  const [checked, setChecked] = useState(false);
  const [revokeGuardian, setRevokeGuardian] = useState<OptionType | null>(null);
  const [disclaimer] = useState(ConsentGRI[1].DisclaimerText);
  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const isAuthorized =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
        permissions['backend.in_active_referrals'].canEdit;
  const [hasEditPermission, setHasEditPermission] = useState<boolean>(false);
  const location = useLocation();
  const consentIdCheck =
    location?.state && location?.state.LEA
      ? location.state.LEA.consentId || ''
      : '';

  useEffect(() => {
    if (leaReferral !== null) {
      if (leaReferral.signature.length > 0) {
        if (
          leaReferral.signature[0].id === '' &&
          leaReferral.signature[0].name === ''
        ) {
          setContactSelected(null);
        } else {
          setContactSelected({
            id: leaReferral.signature[0].id,
            label: leaReferral.signature[0].name,
          });
        }
      }
      if (leaReferral.revokeSignature) {
        setRevokeGuardian({
          id: leaReferral.revokeSignature.id,
          label: leaReferral.revokeSignature.name,
        });
      } else {
        setRevokeGuardian(null);
      }
      setChecked(leaReferral.refusal);
      setLeaReferral(leaReferral);
    }
  }, [leaReferral]);
  useEffect(() => {
    if (!hasEditPermission && isAuthorized) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasEditPermission, isAuthorized]);

  useLayoutEffect(() => {
    const getOfflineMode = async () => {
      const offlineFlag = await checkOffline();
      setIsOffline(offlineFlag);
    };
    getOfflineMode();
  });

  useEffect(() => {
    if (lookups === null) {
      return;
    }
    setEiService(() => {
      return lookups.eiService?.map((e) => {
        return {
          label: e.description,
          id: e.code,
        };
      });
    });

    setLocationOfService(() => {
      return lookups.locationOfService?.map((l) => {
        return {
          label: l.description,
          id: l.code,
        };
      });
    });

    setPeriod(() => {
      return lookups.period?.map((p) => {
        return {
          label: p.description,
          id: p.code,
        };
      });
    });

    setProvidedBy(() => {
      return lookups.ifspDiscipline?.map((p) => {
        return {
          label: p.description,
          id: p.code,
        };
      });
    });

    setIntensity(() => {
      return lookups.intensity
        ? lookups.intensity.map((i) => {
            return {
              label: i.description,
              id: i.code,
            };
          })
        : [];
    });
    setLookUpReasons(() => {
      return lookups.reason
        ? lookups.reason.map((e) => {
            return {
              label: e.description,
              id: e.code,
            };
          })
        : [];
    });
  }, [lookups]);

  const handleParentAndEmployeeSignatures = (data: LEAReferralType) => {
    if (data.employeeSignature === null) {
      data.employeeSignature = {
        signatureValue: '',
        partitionkey: '',
        rowkey: '',
        name: formatName(
          localStorage.getItem('userFirstName'),
          null,
          localStorage.getItem('userLastName'),
          null,
        ),
        date: '',
      };
    } else {
      data.employeeSignature = {
        ...data.employeeSignature,
        name:
          formatName(
            localStorage.getItem('userFirstName'),
            null,
            localStorage.getItem('userLastName'),
            null,
          ) ?? '',
      };
    }
    return data;
  };

  const onSignatureSaveOrUpdate = (signed: string, type: string) => {
    if (type === 'parent') {
      const sig = formRef.current?.values.parentSignature;
      if (sig && sig !== null) {
        formRef.current?.setFieldValue('parentSignature', {
          ...sig,
          partitionkey: '',
          rowkey: '',
          signatureValue: signed,
          date: dayjs().format('MM/DD/YYYY'),
        });
        setParentSignatureClicked(false);
      }
    } else {
      const sig = formRef.current?.values.employeeSignature;
      if (sig && sig !== null) {
        formRef.current?.setFieldValue('employeeSignature', {
          ...sig,
          partitionkey: '',
          rowkey: '',
          signatureValue: signed,
          date: dayjs().format('MM/DD/YYYY'),
        });
        setEmployeeSignatureClicked(false);
      }
    }
  };

  const resetCallBackForTypeEmployee = () => {
    setEmployeeSignatureClicked(!employeeSignatureClicked);
    const sig = formRef.current?.values.employeeSignature;
    if (sig && sig !== null) {
      formRef.current?.setFieldValue('employeeSignature', {
        ...sig,
        partitionkey: '',
        rowkey: '',
        signatureValue: '',
        date: '',
      });
    }
  };
  const handleSuccess = (successError: AlertColor, id: string, mes: string) => {
    setToastrVariable(successError);
    setToastrId(id);
    setToastrDefaultMessage(mes);
  };
  const callLeaReferralAPI = () => {
    const clientId = localStorage.getItem('ClientId');
    const userId = localStorage.getItem('userId');
    if (clientId === null || userId === null) {
      return;
    }
    toggleLoader(true);
    getLEAPreschoolInfo(userId, clientId, consentIdCheck)
      .then(async (response) => {
        toggleLoader(false);
        setChecked(response.refusal);
        const data = handleParentAndEmployeeSignatures(response);
        if (
          response?.signature &&
          response.signature.length == 0 &&
          (response?.parentSignature === null ||
            response?.parentSignature?.signatureValue === '')
        ) {
          response.signature = [
            {
              id: '',
              name: '',
              relationship: '',
              email: '',
              signature: '',
              date: '',
              rowKey: '',
              verbal: false,
              paperConsent: false,
              firstName: '',
              lastName: '',
              isLegal: false,
              isPrimary: false,
              isGuardian: false,
            },
          ];
        }
        if (
          (response.signedDate && response.signedDate !== '') ||
          (response.revokedDate && response.revokedDate !== '')
        ) {
          setEnableDownload(true);
        }
        if (
          (response.signature !== null &&
            response.signature &&
            response.signature[0].signature !== '') ||
          response.signature[0].rowKey !== '' ||
          response.signature[0].partitionKey !== ''
        ) {
          const updatedBy =
            response.lastUpdatedByInfo &&
            formatName(
              response.lastUpdatedByInfo.firstName,
              '',
              response.lastUpdatedByInfo.lastName,
              '',
            );

          if (updatedBy) {
            setLastUpdatedBy(updatedBy);
          }
        }

        if (data.tpc !== null && data.tpc.time !== null) {
          data.tpc.time = moment(data.tpc.time, 'YYYY-MM-DDTHH:mm:ssZ')
            .utc()
            .local()
            .format();
        }

        const reasonsSelected: string[] = [];
        if (data.areasOfConcern !== null) {
          if (data.areasOfConcern.adaptive !== null) {
            reasonsSelected.push('ADP');
          }
          if (data.areasOfConcern.communication !== null) {
            reasonsSelected.push('COM');
          }
          if (data.areasOfConcern.socialEmotional !== null) {
            reasonsSelected.push('S-E');
          }
          if (data.areasOfConcern.motor !== null) {
            reasonsSelected.push('MOT');
          }
          if (data.areasOfConcern.cognitive !== null) {
            reasonsSelected.push('COG');
          }
          if (data.areasOfConcern.otherConcern !== null) {
            reasonsSelected.push('OTHER');
          }
          if (data.areasOfConcern.vision !== null) {
            reasonsSelected.push('VISION');
          }
          if (data.areasOfConcern.hearing !== null) {
            reasonsSelected.push('HEARING');
          }
          if (data.areasOfConcern.behavior !== null) {
            reasonsSelected.push('BEHAVIOR');
          }
          setReferralReasonsSelected(reasonsSelected);
          setInitialReferralReasonsSelectedValue(reasonsSelected);
        }

        const statusCheck = response.authorizationStatus
          ? response.authorizationStatus === 'Revoked'
            ? 'Revoked'
            : response.authorizationStatus
          : 'Not Current';

        if (statusCheck === 'Revoked') {
          setHasEditPermission(false);
        } else if (response.signature.length > 0) {
          if (
            response.signature[0].rowKey ||
            response.signature[0].verbal ||
            response.signature[0].paperConsent
          ) {
            setHasEditPermission(false);
          } else {
            setHasEditPermission(true);
          }
        } else if (response.signature.length === 0) {
          setHasEditPermission(true);
        }

        if (response.signature.length > 0) {
          if (
            response.signature[0].id === '' &&
            response.signature[0].name === ''
          ) {
            setContactSelected(null);
          } else {
            setContactSelected({
              id: response.signature[0].id,
              label: response.signature[0].name,
            });
          }
        }

        if (response.revokeSignature) {
          setRevokeGuardian({
            id: response.revokeSignature.id,
            label: response.revokeSignature.name,
          });
        } else {
          setRevokeGuardian(null);
        }
        setLeaReferral(data);
        setLeaReferralBackup(data);

        await getContacts(response, response.contact, response.uiVersion);
      })
      .catch(async (error) => {
        setToastrVariable('error');
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('formBuilder.EditFail');
          setToastrDefaultMessage('Failed to load form');
        }
      });
  };

  useEffect(() => {
    callLeaReferralAPI();
  }, []);

  const updateReferralReasonsChecks = (checkedVal: boolean, id: string) => {
    if (checkedVal) {
      setReferralReasonsSelected([...referralReasonsSelected, id]);
    } else {
      setReferralReasonsSelected(
        referralReasonsSelected.filter((item) => item !== id),
      );
    }
  };

  const handleFormSubmission = useCallback(
    debounce((values: LEAReferralType) => {
      handleLeaSubmission(values);
    }, 500),
    [],
  );
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };
  const handleLeaSubmission = (lea: LEAReferralType) => {
    const clientId = localStorage.getItem('ClientId');
    const userId = localStorage.getItem('userId');
    if (clientId === null || userId === null) {
      return;
    }
    if (
      lea.signature.length > 0 &&
      (lea.signature[0].rowKey ||
        lea.signature[0].signature ||
        lea.signature[0].verbal ||
        lea.signature[0].paperConsent) &&
      (!lea.signature[0].consentSignedDate ||
        !dayjs(lea.signature[0].consentSignedDate).isValid())
    ) {
      setOpen(true);
      setToastrVariable('error');
      setToastrDefaultMessage('Consent date is Required');
      setToastrId('ConsentForms.consentDateIsRequired');
      return;
    }

    const resultStatus =
      !lea.refusal &&
      lea.signature.some((s) => {
        if (s.name === '') return false;

        if (s.verbal || s.paperConsent || s.signature !== '' || s.rowKey !== '')
          return false;

        return true;
      });
    if (resultStatus) {
      setOpen(true);
      setToastrVariable('error');
      setToastrId('ConsentForms.signatureToasterError');
      setToastrDefaultMessage(
        'Guardian Signature / Verbal Consent is mandatory',
      );
      return;
    }
    if (lea.refusal && lea.revokeSignature === null) {
      setOpen(true);
      setToastrVariable('error');
      setToastrDefaultMessage('Revoking consent require guardian name');
      setToastrId('revokeGuardianNameRequired');
      return;
    }
    if (lea.refusal && lea.revokeSignature) {
      const signature = lea.revokeSignature;
      if (
        ((lea.uiVersion != null && lea.uiVersion <= 2 && !signature.verbal) ||
          (lea.uiVersion != null &&
            lea.uiVersion >= 3 &&
            !signature.paperConsent)) &&
        signature.signature === '' &&
        !signature.rowKey
      ) {
        setOpen(true);
        setToastrVariable('error');
        setToastrDefaultMessage('Revoking consent require guardian signature');
        setToastrId('revokeGuardianNameRequired');
        return;
      }
    }

    if (
      lea.refusal &&
      dayjs(lea.revokedDate).isBefore(dayjs(lea.signedDate).format(DATE_FORMAT))
    ) {
      setOpen(true);
      setToastrVariable('error');
      setToastrDefaultMessage('Refusal Date cannot be before Consent Date');
      setToastrId('ConsentForms.refusalDateCannotBeBeforeToday');
      return;
    }
    if (lea.refusal && !lea.revokedDate) {
      setOpen(true);
      setToastrVariable('error');
      setToastrDefaultMessage('Revoke Date is required for Consent form');
      setToastrId('ConsentForms.revokeDateRequired');
      return;
    }
    if (lea?.tpc && lea.tpc.date !== null && lea.tpc.time !== null) {
      const time = dayjs(lea.tpc.time).format('HH:mm:ss');
      const timeZOne = dayjs(lea.tpc.date).format('Z');
      const utcDateTime = dayjs
        .utc(`${lea.tpc.date} ${time}${timeZOne}`)
        .format();
      lea.tpc.time = utcDateTime;
    }

    let revokedDateValue = '';
    if (lea.refusal) {
      revokedDateValue = lea.revokedDate
        ? dayjs(lea.revokedDate, DATE_FORMAT)
            .hour(dayjs().hour())
            .minute(dayjs().minute())
            .second(dayjs().second())
            .millisecond(dayjs().millisecond())
            .toISOString()
        : '';
    }

    let signedDate: string | Date = '';
    if (
      lea.signature.length > 0 &&
      lea.signature.some(
        (item) =>
          item.signature !== '' ||
          item.verbal ||
          item.paperConsent ||
          item.rowKey !== '',
      )
    ) {
      lea.signature.forEach((item) => {
        if (
          item.signature !== '' ||
          item.verbal ||
          item.paperConsent ||
          item.rowKey !== ''
        ) {
          signedDate =
            dayjs(lea.signature[0].consentSignedDate, DATE_FORMAT)
              .hour(dayjs().hour())
              .minute(dayjs().minute())
              .second(dayjs().second())
              .millisecond(dayjs().millisecond())
              .toISOString() ?? '';
        }
      });
    }

    lea.signedDate = signedDate;
    lea.revokedDate = revokedDateValue;
    lea.consentId = consentIdCheck;
    lea.signature[0].date = '';

    toggleLoader(true);

    updateLEAPreschoolInfo(userId, clientId, lea)
      .then(() => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId('ConsentForms.submitToaster');
        setToastrDefaultMessage('Consent Form Successfully Saved.');
        setTimeout(() => {
          toggleLoader(false);
          navigate(LEA_REFERRAL_DASHBOARD);
        }, 1000);
      })
      .catch((error) => {
        toggleLoader(false);
        setToastrVariable('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('formBuilder.EditFail');
          setToastrDefaultMessage('Failed to update form');
        }
      });
  };

  const handlePreSchoolDownload = (language: string) => {
    toggleLoader(true);
    const clientId = localStorage.getItem('ClientId');
    const userId = localStorage.getItem('userId');
    const localTimezone = dayjs.tz.guess();
    if (clientId && userId) {
      downloadLeaPreSchool(
        userId,
        clientId,
        localTimezone,
        consentIdCheck,
        language,
      )
        .then((response) => {
          const blob = new Blob([response], { type: 'application/pdf' });
          // Create a Blob URL
          const blobUrl = URL.createObjectURL(blob);
          // Open the Blob URL in a new tab
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.target = '_blank';
          anchor.download = getClientDocumentFileName(
            `LEA Referral and TPC.pdf`,
            leaReferral.signedDate || null,
          ); // Set desired file name here
          // Append anchor to document body
          document.body.appendChild(anchor);
          // Trigger anchor click
          anchor.click();
          window.open(blobUrl, '_blank');
          // Clean up after use
          URL.revokeObjectURL(blobUrl);
          document.body.removeChild(anchor);
          toggleLoader(false);
        })
        .catch((error) => {
          toggleLoader(false);
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrVariable('error');
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrVariable('error');
            setToastrId('ConsentForms.downloadErrorMsg');
            setToastrDefaultMessage('Failed to download');
          }
        });
    }
  };
  const checkedGuardianRequired = () => {
    return checked;
  };
  const onSaveClick = (sign: string, id: number) => {
    setLeaReferral((prevConsentDetails) => {
      const updatedSignature = prevConsentDetails.signature.map(
        (item, index) => {
          if (index === id) {
            return {
              ...item,
              signature: sign,
              date: '',
              partitionKey: '',
              rowKey: '',
            };
          }
          return item;
        },
      );

      return {
        ...prevConsentDetails,

        signature: updatedSignature,
      };
    });
  };
  const handleStatus = (statusValue: string) => {
    let validatedStatus = '';
    if (statusValue.toLowerCase().includes('revoked')) {
      validatedStatus = 'Revoked'.concat(
        ' (' +
          moment(dayjs(leaReferral.revokedDate).toDate()).format('MM/DD/YYYY') +
          ')',
      );
    } else {
      validatedStatus = statusValue;
    }

    return validatedStatus;
  };
  const onVerbalConsentClick = (value: boolean, index: number) => {
    const signatureDate = moment().format('MM/DD/YYYY');
    setLeaReferral((prevConsentDetails) => {
      const updatedSignature = prevConsentDetails.signature.map((item, id) => {
        if (id === index) {
          return {
            ...item,
            verbal: value,
            date: signatureDate,
          };
        }
        return item;
      });

      return {
        ...prevConsentDetails,
        signature: updatedSignature,
      };
    });
  };

  const onPaperConsentClick = (value: boolean, index: number) => {
    const signatureDate = moment().format('MM/DD/YYYY');
    setLeaReferral((prevConsentDetails) => {
      const updatedSignature = prevConsentDetails.signature.map((item, id) => {
        if (id === index) {
          return {
            ...item,
            paperConsent: value,
            date: signatureDate,
          };
        }
        return item;
      });

      return {
        ...prevConsentDetails,
        signature: updatedSignature,
      };
    });
  };
  const isSignatureInProgress = () => {
    return (
      leaReferral.signature?.some((s, index) => {
        const persistedSignature = leaReferralBackup.signature?.[index];
        const hasSignatureData =
          s.name !== '' ||
          s.signature !== '' ||
          s.verbal ||
          s.paperConsent ||
          s.rowKey !== '' ||
          (index === 0 && contactSelected !== null);
        const wasSaved =
          persistedSignature !== undefined &&
          (!!persistedSignature.signature ||
            persistedSignature.verbal === true ||
            persistedSignature.paperConsent === true ||
            !!persistedSignature.rowKey);
        return hasSignatureData && !wasSaved;
      }) ?? false
    );
  };

  const onChecked = (value: boolean) => {
    if (!leaReferral) return;

    setLeaReferral((prevLeaReferral) => ({
      ...prevLeaReferral,
      refusal: value,
      revokeSignature: value ? prevLeaReferral.revokeSignature : null,
      revokedDate: value ? prevLeaReferral.revokedDate : null,
    }));
    if (!value) setRevokeGuardian(null);
    setChecked(value);
  };

  const renderSignatureComponent = () => {
    return (
      <Box component="div" style={{ margin: '10px' }}>
        <MediumTypography
          fontSize="18px"
          fontweight={600}
          textColor="#2A4241"
          labelid={'ConsentForms.signatureTitle'}
          defaultlabel={'Signature'}
          className="pt-md"
        />

        <>
          {((leaReferral.uiVersion ?? 1) >= 2 ||
            leaReferral.signature?.length > 0) && (
            <DropdownComponent
              labelid="sspReferral.contact"
              defaultlabelid="Select Guardian"
              names={guardianContacts}
              disabled={
                !hasEditPermission || !isAuthorized || leaReferral.refusal
              }
              Required={checkedGuardianRequired()}
              handleChange={(value) => {
                if (value === '') {
                  setContactSelected(null);
                  const signatures = leaReferral.signature.map(
                    (sign: ConsentClientSignature) => ({
                      ...sign,
                      name: '',
                      email: '',
                      signature: '',
                      verbal: false,
                      paperConsent: false,
                      partitionKey: '',
                      rowKey: '',
                      date: '',
                      id: '',
                      isPrimary: false,
                      relationship: '',
                      consentSignedDate: '',
                      isGuardian: false,
                      isLegal: false,
                    }),
                  );
                  setLeaReferral({
                    ...leaReferral,
                    signedDate: null,
                    signature: signatures,
                  });
                } else {
                  const foundContact = guardianContacts.find(
                    (contact) => contact.id === value,
                  );
                  if (foundContact) {
                    const contact = leaReferral?.contact.find(
                      (c) => c.id.toString() === foundContact.id,
                    );
                    if (contact) {
                      const signatures = leaReferral?.signature.map(
                        (sign: ConsentClientSignature) => ({
                          ...sign,
                          name: contact.name,
                          email: contact.email,
                          signature: '',
                          verbal: false,
                          paperConsent: false,
                          partitionKey: '',
                          rowKey: '',
                          isGuardian: contact.isGuardian,
                          isPrimary: contact.isPrimary,
                          isLegal: contact.isLegalGuardian,
                          relationship: contact.type ?? '',
                          id: contact.id,
                          date: moment(sign.date, 'YYYY-MM-DDTHH:mm:ss')
                            .utc(true)
                            .local()
                            .format(DATE_FORMAT),
                        }),
                      );

                      setLeaReferral({
                        ...leaReferral,
                        signature: signatures,
                      });
                    }

                    setContactSelected(foundContact);
                  }
                }
              }}
              value={contactSelected?.id || ''}
              className="mt-md width400px"
            />
          )}

          <ConsentSignature
            signatureData={leaReferral.signature}
            staffSignature={false}
            revokeChecked={leaReferral.refusal}
            onSaveClick={onSaveClick}
            verbalConsentValue={leaReferral.signature}
            paperConsentValue={leaReferral.signature}
            onVerbalConsentClick={onVerbalConsentClick}
            onPaperConsentClick={onPaperConsentClick}
            defaultParentGuardianNoId="Guardian Name"
            parentGuardianNoId="ConsentParentGuardianNoId"
            relationshipWithChildId="ConsentRelationshipWithChild"
            defaultRelationshipWithChildId="Guardian Type:"
            emailId="ConsentEmail"
            defaultEmailId="Email:"
            isDateRequired={
              leaReferral?.signature?.length > 0
                ? leaReferral.signature[0].name !== ''
                : false
            }
            viewUpdateId="ConsentViewOrUpdateSignature"
            defaultViewUpdateId="View/update signature"
            addSignatureId="ConsentAddSignature"
            defaultAddSignatureId="Add Signature"
            verbalConsentId="ConsentVerbalConsent"
            defaultVerbalConsentId="Verbal Consent"
            defaultParentSignatureId="Guardian Signature"
            isPaperConsentRequired={(leaReferral?.uiVersion ?? 1) > 2}
            isVerbalConsentRequired={(leaReferral?.uiVersion ?? 1) <= 2}
            paperConsentId="PaperConsent"
            defaultPaperConsentId="Paper Consent"
            onConsentSignedDateChange={onSignatureConsentDateChange}
            hasEditPermission={!hasEditPermission || !isAuthorized}
          />
        </>
      </Box>
    );
  };
  const renderParentSignatureComponent = (values: LEAReferralType) => {
    return (
      <Grid item xs={12} display={'flex'}>
        <Grid item xs={4} display={'flex'}>
          <Box className="pb-md pt-md">
            <MediumTypography
              labelid="Insurance.Parentsignature"
              defaultlabel="Guardian Signature"
              className="mainText-xlg pt-md"
            />
            {values.parentSignature !== null &&
              (values.parentSignature.signatureValue !== '' ||
                values.parentSignature.rowkey !== '') && (
                <>
                  <SignatureName
                    date={values.parentSignature.date}
                    name={values.parentSignature.name}
                  />
                  <ButtonComponent
                    className="btn-primary btn-cancel signature_view_Box"
                    labelId={'ProgressNoteViewSignature'}
                    defaultLabelId={'View signature'}
                    variantType="outlined"
                    LeftIcon={<Editsignature />}
                    onClick={() => setParentSignatureClicked(true)}
                  />
                </>
              )}
            {values.parentSignature !== null &&
              values.parentSignature.signatureValue === '' &&
              values.parentSignature.rowkey === '' && (
                <>
                  <SignatureName
                    date={values.parentSignature.date}
                    name={values.parentSignature.name}
                  />
                  <ButtonComponent
                    className="btn-primary btn-cancel signature_add_Box mt-xs"
                    labelId="Insurance.Addsignature"
                    defaultLabelId="Add Signature"
                    variantType="outlined"
                    onClick={() => setParentSignatureClicked(true)}
                  />
                </>
              )}
            {parentSignatureClicked && (
              <Box component="div">
                <SignatureComponent
                  onCancelClick={() => setParentSignatureClicked(false)}
                  signaturePartitionKey={
                    values.parentSignature !== null
                      ? values.parentSignature.partitionkey
                      : ''
                  }
                  signatureRowKey={
                    values.parentSignature !== null
                      ? values.parentSignature.rowkey
                      : ''
                  }
                  signature={
                    values.parentSignature !== null
                      ? values.parentSignature.signatureValue
                      : ''
                  }
                  signatureSaveOrUPdate={(value) =>
                    onSignatureSaveOrUpdate(value, 'parent')
                  }
                  handleSuccess={handleSuccess}
                  signClicked={parentSignatureClicked}
                  hideRemoveButton={false}
                  hideResetButton={true}
                  resetCallBackForTypeEmployee={resetCallBackForTypeEmployee}
                  type="Parent"
                />
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={4} display={'flex'} sx={{ display: 'none' }}>
          <Box className="pb-md pt-md">
            <MediumTypography
              labelid="EmployeeSignatureText"
              defaultlabel="Employee Signature"
              className="mainText-xlg pt-md"
            />

            {values.employeeSignature !== null &&
              (values.employeeSignature.signatureValue !== '' ||
                values.employeeSignature.rowkey !== '') && (
                <>
                  <SignatureName
                    date={values.employeeSignature.date}
                    name={values.employeeSignature.name}
                  />
                  <ButtonComponent
                    className="btn-primary btn-cancel signature_view_Box"
                    labelId={'ProgressNoteViewSignature'}
                    defaultLabelId={'View signature'}
                    variantType="outlined"
                    LeftIcon={<Editsignature />}
                    onClick={() => setEmployeeSignatureClicked(true)}
                  />
                </>
              )}

            {values.employeeSignature !== null &&
              values.employeeSignature.signatureValue === '' &&
              values.employeeSignature.rowkey === '' && (
                <>
                  <SignatureName
                    date={values.employeeSignature.date}
                    name={values.employeeSignature.name}
                  />
                  <ButtonComponent
                    className="btn-primary btn-cancel signature_add_Box mt-xs"
                    labelId="Insurance.Addsignature"
                    defaultLabelId="Add Signature"
                    variantType="outlined"
                    onClick={() => setEmployeeSignatureClicked(true)}
                  />
                </>
              )}
            {employeeSignatureClicked && (
              <Box component="div">
                <SignatureComponent
                  onCancelClick={() => setEmployeeSignatureClicked(false)}
                  signaturePartitionKey={
                    values.employeeSignature !== null
                      ? values.employeeSignature.partitionkey
                      : ''
                  }
                  signatureRowKey={
                    values.employeeSignature !== null
                      ? values.employeeSignature.rowkey
                      : ''
                  }
                  signature={
                    values.employeeSignature !== null
                      ? values.employeeSignature.signatureValue
                      : ''
                  }
                  signatureSaveOrUPdate={(value) =>
                    onSignatureSaveOrUpdate(value, 'employee')
                  }
                  handleSuccess={handleSuccess}
                  signClicked={employeeSignatureClicked}
                  hideRemoveButton={false}
                  resetCallBackForTypeEmployee={resetCallBackForTypeEmployee}
                  type="Employee"
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  };

  const onSignatureConsentDateChange = (
    signedDate: string | null,
    index: number,
  ) => {
    setLeaReferral((prevLeaReferral) => {
      const updatedSignature = prevLeaReferral.signature.map((sign, id) => {
        if (id === index) {
          return {
            ...sign,
            consentSignedDate: signedDate,
          };
        }
        return sign;
      });
      return {
        ...prevLeaReferral,
        signature: updatedSignature,
      };
    });
  };

  const onRevokeConsentSignDateChange = (signedDate: string | null) => {
    const updatedRevokeSignature = leaReferral.revokeSignature
      ? { ...leaReferral.revokeSignature, consentSignedDate: signedDate }
      : leaReferral.revokeSignature;
    setLeaReferral({
      ...leaReferral,
      revokedDate: signedDate,
      revokeSignature: updatedRevokeSignature,
    });
  };

  const onRevokeSignatureVerbalConsentClick = (value: boolean) => {
    const signedDate = dayjs().utc().format(LOCAL_DATE_TIME_UTC_FORMAT);
    const formatedDate = dayjs(signedDate).format(DATE_FORMAT);
    const updatedRevokeSignature = leaReferral.revokeSignature
      ? {
          ...leaReferral.revokeSignature,
          verbal: value,
          consentSignedDate:
            leaReferral.revokeSignature.consentSignedDate || formatedDate,
        }
      : leaReferral.revokeSignature;
    if (updatedRevokeSignature) {
      setLeaReferral({
        ...leaReferral,
        revokedDate: updatedRevokeSignature.consentSignedDate,
        revokeSignature: updatedRevokeSignature,
      });
    }
  };

  const onRevokeSignaturePaperConsentClick = (value: boolean) => {
    const signedDate = dayjs().utc().format(LOCAL_DATE_TIME_UTC_FORMAT);
    const formatedDate = dayjs(signedDate).format(DATE_FORMAT);
    const updatedRevokeSignature = leaReferral.revokeSignature
      ? {
          ...leaReferral.revokeSignature,
          paperConsent: value,
          consentSignedDate:
            leaReferral.revokeSignature.consentSignedDate || formatedDate,
        }
      : leaReferral.revokeSignature;
    if (updatedRevokeSignature) {
      setLeaReferral({
        ...leaReferral,
        revokedDate: updatedRevokeSignature.consentSignedDate,
        revokeSignature: updatedRevokeSignature,
      });
    }
  };

  const onRevokeSignatureSaveClick = (sign: string) => {
    const signedDate = dayjs().utc().format(LOCAL_DATE_TIME_UTC_FORMAT);
    const formatedDate = dayjs(signedDate).format(DATE_FORMAT);
    const revokedSignature = leaReferral.revokeSignature
      ? { ...leaReferral.revokeSignature, signature: sign }
      : leaReferral.revokeSignature;

    if (revokedSignature && !revokedSignature.consentSignedDate) {
      revokedSignature.consentSignedDate = formatedDate;
    }
    if (revokedSignature) {
      setLeaReferral({
        ...leaReferral,
        revokedDate: revokedSignature.consentSignedDate ?? formatedDate,
        revokeSignature: revokedSignature,
      });
    }
  };
  const getConsentMinimumDate = (): dayjs.Dayjs | undefined => {
    const signatureWithDate = leaReferral.signature?.find(
      (s) => s.consentSignedDate && s.consentSignedDate !== '',
    );
    if (signatureWithDate) {
      return dayjs(signatureWithDate.consentSignedDate);
    }
    if (leaReferral.signedDate && leaReferral.signedDate !== '') {
      return dayjs(leaReferral.signedDate);
    }
    return undefined;
  };
  const getContacts = (
    responseData: LEAReferralType,
    responseContact: ConsentClientContact[],
    uiVersionVal: number | null,
  ) => {
    if (responseContact) {
      const contactList: OptionType[] = [];
      const guardianList: OptionType[] = [];
      const parents: Parent[] = [];
      if (
        uiVersionVal === 1 &&
        (responseData?.parentSignature?.signatureValue !== '' ||
          responseData?.parentSignature?.rowkey !== '')
      ) {
        const parent = {
          contactId: responseData?.parent?.contactId ?? '',
          name: responseData?.parent?.name,
          homePhoneNumber: responseData?.parent?.homePhoneNumber,
          isPrimary: responseData?.parent?.isPrimary,
          type: responseData?.parent?.type ?? '',
          relationshipToClient: null,
          email: responseData?.parent?.email,
          address: responseData?.parent?.address,
        };
        parents.push(parent);
        setGuardianContactsArray(parents);

        responseContact
          .filter((contact) => contact.isGuardian)
          .forEach((contact) => {
            guardianList.push({
              id: contact.id.toString(),
              label: contact.name,
            });
          });
        setGuardianContacts(guardianList);
      } else {
        responseContact.forEach((contact) => {
          const prefixes = [
            contact.isPrimary && 'Primary',
            contact.isLegalGuardian && 'Legal',
          ].filter(Boolean);

          if (contact.type === 'Referral Contact') {
            contactList.push({
              id: contact.id.toString(),
              label: contact.name ?? 'Referral Contact',
            });
          } else if (contact.type !== 'Family') {
            contactList.push({
              id: contact.id.toString(),
              label: contact.name,
            });
          } else if (leaReferral && leaReferral?.selectedContact?.length > 0) {
            const hasSelectedContact = leaReferral?.selectedContact.some(
              (data) => {
                return data.toString() === contact.id.toString();
              },
            );
            if (hasSelectedContact) {
              contactList.push({
                id: contact.id.toString(),
                label: contact.name,
              });
            }
          }
          if (contact.isGuardian) {
            guardianList.push({
              id: contact.id.toString(),
              label: `${contact.name}${
                prefixes.length > 0 ? ` - (${prefixes.join(', ')})` : ''
              }`,
            });
          }
          if (contact.isGuardian) {
            const parent: Parent = {
              contactId: '',
              name: `${contact.name}${
                prefixes.length > 0 ? ` - (${prefixes.join(', ')})` : ''
              }`,
              homePhoneNumber: contact.phone,
              isPrimary: contact.isPrimary,
              type: contact.type ?? '',
              relationshipToClient: null,
              email: contact.email,
              address: contact.address,
            };
            parents.push(parent);
          }
        });

        setGuardianContactsArray(parents);

        setGuardianContacts(guardianList);
      }
    }
    return [];
  };

  return (
    <Box component="main">
      <SnackBarComponent
        open={open}
        handleClose={handleClose}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <LanguageSelectionDialog
        open={openDownloadLanguageModal}
        onClose={() => setOpenDownloadLanguageModal(false)}
        onConfirm={(language) => {
          setOpenDownloadLanguageModal(false);
          handlePreSchoolDownload(language);
        }}
      />

      <ModalPopup
        description="formUnsavedChangesMessage"
        open={openModal}
        onOk={() => {
          formRef.current?.setValues(formRef.current?.initialValues);
          setReferralReasonsSelected(initialReferralReasonsSelectedValue);

          setOpenModal(false);
        }}
        onCancel={() => setOpenModal(false)}
        labelId1="Clientpage.cancelbtn"
        negativeActionLabel="cancelText"
        labelId2="Clientpage.Okbtn"
        positiveActionLabel="deleteText"
      />

      <Box>
        <Card className="p-lg">
          {!isOffline && (
            <Box className="flex__ justifyContent-FlexEnd">
              <ButtonComponent
                className="btn-primary btn-submit mb-xlg"
                labelId="ConsentForms.listActionDownloadToolTipLabel"
                defaultLabelId="Download"
                variantType="contained"
                disabled={!enableDownload}
                onClick={() => setOpenDownloadLanguageModal(true)}
              />
            </Box>
          )}
          <Box ref={parentRef}>
            <Box component="section">
              <ProgramPanel
                program={leaReferral.program}
                client={leaReferral.client}
                parents={guardianContactsArray}
                showIntakeFacilitator={false}
                showAdditionalDemographics={true}
              />
            </Box>
            <SingleLine></SingleLine>
            <Box component="div">
              <ConsentGeneralInfo
                consentDatePlaceholder={'ConsentForms.signedDate'}
                consentDateDefaultPlaceholder={'Consent Date:'}
                expiryDatePlaceholder={'ConsentForms.revokedDate'}
                expiryDateDefaultPlaceholder={'Expiry Date:'}
                status={handleStatus(leaReferral?.authorizationStatus ?? '')}
                signedDate={
                  leaReferral.signedDate
                    ? moment(leaReferral.signedDate)
                        .local()
                        .format('MM/DD/YYYY')
                    : ''
                }
                expiredDate={''}
                statusLabelId={'ConsentForms.status'}
                statusDefaultLabelId={'Authorization Status:'}
                expireDateView={true}
              />
              <SingleLine className="width__100 mt-sm mb-sm" />
            </Box>

            <Formik
              innerRef={formRef}
              initialValues={leaReferral}
              enableReinitialize={true}
              validationSchema={validationSchema}
              validate={validateForm}
              onSubmit={(values) => {
                handleFormSubmission(structuredClone(values));
              }}
            >
              {({ values, setFieldValue, errors, touched }) => {
                return (
                  <>
                    <UnsavedChangesWatcher
                      values={values}
                      initialValues={formRef.current?.initialValues}
                      currentValues={formRef.current?.values}
                      onUnsavedChangesChange={handleUpdateUnsavedChanges}
                    />
                    <Box
                      component="div"
                      style={{
                        pointerEvents:
                          hasEditPermission && isAuthorized ? 'auto' : 'none',
                        opacity:
                          hasEditPermission && isAuthorized ? 'unset' : '0.8',
                      }}
                    >
                      <Box
                        component="div"
                        className="mt-md"
                        sx={{
                          pointerEvents:
                            hasEditPermission && isAuthorized ? 'auto' : 'none',
                        }}
                      >
                        <Grid
                          container
                          xs={12}
                          rowSpacing={'16px'}
                          columnSpacing={2}
                          className="mt-lg"
                        >
                          <Grid item xs={12}>
                            <MediumTypography
                              sxProps={{ fontSize: '16px' }}
                              labelid="BirthPlaceText "
                              defaultlabel="Birth Place"
                            />
                          </Grid>

                          <Grid item xs={3}>
                            <Textinput
                              disabled={!isAuthorized}
                              Value={
                                values.birthPlaceCity !== null
                                  ? values.birthPlaceCity
                                  : ''
                              }
                              handlechange={(e) => {
                                setFieldValue(
                                  'birthPlaceCity',
                                  nameValidation(e),
                                );
                                setLeaReferral({
                                  ...leaReferral,
                                  birthPlaceCity: nameValidation(e),
                                });
                              }}
                              labelid="parentGuardian.city"
                              defaultlabelid="City"
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <Textinput
                              disabled={!isAuthorized}
                              Value={
                                values.birthPlaceState !== null
                                  ? values.birthPlaceState
                                  : ''
                              }
                              handlechange={(e) => {
                                setFieldValue(
                                  'birthPlaceState',
                                  nameValidation(e),
                                );
                                setLeaReferral({
                                  ...leaReferral,
                                  birthPlaceState: nameValidation(e),
                                });
                              }}
                              labelid="parentGuardian.state"
                              defaultlabelid="State"
                            />
                          </Grid>
                          <Grid item xs={9}>
                            <MediumTypography
                              sxProps={{ fontSize: '16px' }}
                              labelid="interpreterRequiredText"
                              defaultlabel="Interpreter Needed"
                            />
                          </Grid>
                          <Grid item xs={6} lg={6}>
                            <MultipleAutocompleteSearch
                              labelId="interpreterRequiredText"
                              defaultId="Interpreter Needed"
                              data={interPreterOptions}
                              type="string"
                              onSelectItem={(value: OptionType[] | null) => {
                                if (value) {
                                  const selectedRaceIds = value.map(
                                    (r) => r.id,
                                  );

                                  setFieldValue(
                                    'interpreterNeeded',
                                    selectedRaceIds.toString(),
                                  );
                                  setLeaReferral({
                                    ...leaReferral,
                                    interpreterNeeded:
                                      selectedRaceIds.toString(),
                                  });
                                }
                              }}
                              disableLabel={true}
                              optionHintId={'No Race found'}
                              sxProps={{
                                [`& .${autocompleteClasses.popupIndicator}`]: {
                                  transform: 'none',
                                  marginTop: '2px',
                                },
                                backgroundColor: '#fff',
                              }}
                              value={(values.interpreterNeeded || '')
                                .split(',')
                                .map((r) => ({
                                  id: r,
                                  label:
                                    interPreterOptions.find(
                                      (interpreterValue) =>
                                        interpreterValue.id === r,
                                    )?.label ?? '',
                                }))
                                .filter(
                                  (
                                    option,
                                  ): option is {
                                    id: string;
                                    label: string;
                                  } => option.label !== '',
                                )}
                              disabled={!isAuthorized}
                            />
                            {errors.interpreterNeeded &&
                              touched.interpreterNeeded && (
                                <MediumTypography
                                  labelid={errors.interpreterNeeded}
                                  textColor="red"
                                  marginTop="8px"
                                />
                              )}
                          </Grid>
                          <Grid item xs={12}>
                            <MediumTypography
                              sxProps={{ fontSize: '16px' }}
                              labelid="childResidesWithText"
                              defaultlabel="Child resides with"
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <Textinput
                              disabled={!isAuthorized}
                              Value={
                                values.childResidesWith !== null
                                  ? values.childResidesWith
                                  : ''
                              }
                              handlechange={(e) => {
                                setFieldValue(
                                  'childResidesWith',
                                  nameValidation(e),
                                );
                                setLeaReferral({
                                  ...leaReferral,
                                  childResidesWith: nameValidation(e),
                                });
                              }}
                              labelid="parentGuardianText"
                              defaultlabelid="Child resides with"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MediumTypography
                              sxProps={{ fontSize: '16px' }}
                              labelid="referralToLocalEducationAgencyText"
                              defaultlabel="Referral to the local education agency (LEA) / School district for SPED services"
                            />
                          </Grid>

                          <Grid item xs={4.5}>
                            <DatePickerComponent
                              labelid="dateOfReferralSchoolDistrictText"
                              defaultlabelid="Date of referral to the LEA/School district"
                              value={
                                values.referralDate
                                  ? dayjs(values.referralDate)
                                  : null
                              }
                              required
                              handlechange={(date: Dayjs | null) => {
                                const formattedDate =
                                  dayjs(date).format('MM/DD/YYYY');
                                if (date === null) {
                                  setFieldValue('daysThirdBirth', null);
                                  setFieldValue('referralDate', null);
                                  setLeaReferral({
                                    ...leaReferral,
                                    referralDate: null,
                                    daysThirdBirth: null,
                                  });
                                } else {
                                  const thirdBirthDay = dayjs(
                                    leaReferral.client?.dob,
                                  ).add(3, 'year');
                                  setFieldValue(
                                    'daysThirdBirth',
                                    thirdBirthDay.diff(date, 'days') + 1,
                                  );
                                  setFieldValue('referralDate', formattedDate);
                                  setLeaReferral({
                                    ...leaReferral,
                                    referralDate: formattedDate,
                                    daysThirdBirth:
                                      thirdBirthDay.diff(date, 'days') + 1,
                                  });
                                }
                              }}
                              disabledDate={!isAuthorized}
                            />
                            {errors.referralDate && touched.referralDate && (
                              <MediumTypography
                                label={rctl.formatMessage({
                                  id: errors.referralDate,
                                  defaultMessage: 'Referral Date is Required',
                                })}
                                className="errorText-md"
                              />
                            )}
                          </Grid>

                          <Grid item xs={12}>
                            <MediumTypography
                              sxProps={{ fontSize: '16px' }}
                              labelid="parentsWrittenConsentText"
                              defaultlabel="(With a parent's written consent, a referral must be made at least 90 days and up to 9 months prior to the child's third birthday.)"
                            />
                          </Grid>

                          <Grid item xs={3}>
                            <Textinput
                              disabled={true}
                              Value={
                                values.daysThirdBirth !== null
                                  ? values.daysThirdBirth.toString()
                                  : ''
                              }
                              labelid="daysBODText"
                              defaultlabelid="No. of days to 3rd birthday"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <MediumTypography
                              className="mainText-md"
                              labelid="leaDistrictInformation"
                              defaultlabel="LEA/School District information"
                            />
                          </Grid>
                          <Grid item xs={4.5}>
                            <Textinput
                              labelid="referringToText"
                              defaultlabelid="Referring to"
                              inputProps={{
                                maxLength: 100,
                              }}
                              Required
                              Value={values.referringTo ?? ''}
                              handlechange={(e) => {
                                setFieldValue('referringTo', e);
                                setLeaReferral({
                                  ...leaReferral,
                                  referringTo: e,
                                });
                              }}
                              disabled={!isAuthorized}
                            />
                            {errors.referringTo && touched.referringTo && (
                              <MediumTypography
                                label={rctl.formatMessage({
                                  id: errors.referringTo,
                                  defaultMessage: 'Referring to is required',
                                })}
                                className="errorText-md mt-xs"
                              />
                            )}
                          </Grid>

                          <Grid item xs={12}>
                            <MediumTypography
                              className="mainText-md"
                              labelid="presentIssuesText"
                              defaultlabel="Present/Problem issues"
                            />
                            <Grid item xs={12}>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'ADP',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedADP =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedADP,
                                        'ADP',
                                      );

                                      if (checkedADP) {
                                        const value =
                                          values.areasOfConcern?.adaptive ?? '';

                                        setFieldValue(
                                          'areasOfConcern.adaptive',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            adaptive: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.adaptive',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            adaptive: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="Adaptive (ADP)"
                                    defaultlabelid="Adaptive (ADP)"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes('ADP') && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern?.adaptive ?? ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.adaptive',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            adaptive: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>

                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'COM',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedCOM =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedCOM,
                                        'COM',
                                      );

                                      if (checkedCOM) {
                                        const value =
                                          values.areasOfConcern
                                            ?.communication ?? '';

                                        setFieldValue(
                                          'areasOfConcern.communication',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            communication: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.communication',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            communication: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="Communication (COM)"
                                    defaultlabelid="Communication (COM)"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes('COM') && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern?.communication ??
                                        ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.communication',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            communication: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'S-E',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedADP =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedADP,
                                        'S-E',
                                      );

                                      if (checkedADP) {
                                        const value =
                                          values.areasOfConcern
                                            ?.socialEmotional ?? '';

                                        setFieldValue(
                                          'areasOfConcern.socialEmotional',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            socialEmotional: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.socialEmotional',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            socialEmotional: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="Personal–Social(P-S)"
                                    defaultlabelid="Social-Emotional (S-E)"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes('S-E') && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern
                                          ?.socialEmotional ?? ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.socialEmotional',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            socialEmotional: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'MOT',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedMOT =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedMOT,
                                        'MOT',
                                      );

                                      if (checkedMOT) {
                                        const value =
                                          values.areasOfConcern?.motor ?? '';

                                        setFieldValue(
                                          'areasOfConcern.motor',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            motor: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.motor',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            motor: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="Motor (MOT)"
                                    defaultlabelid="Motor (MOT)"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes('MOT') && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={values.areasOfConcern?.motor ?? ''}
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.motor',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            motor: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'COG',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedCOG =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedCOG,
                                        'COG',
                                      );

                                      if (checkedCOG) {
                                        const value =
                                          values.areasOfConcern?.cognitive ??
                                          '';

                                        setFieldValue(
                                          'areasOfConcern.cognitive',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            cognitive: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.cognitive',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            cognitive: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="Cognitive (COG)"
                                    defaultlabelid="Cognitive (COG)"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes('COG') && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern?.cognitive ?? ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.cognitive',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            cognitive: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'VISION',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedVISION =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedVISION,
                                        'VISION',
                                      );

                                      if (checkedVISION) {
                                        const value =
                                          values.areasOfConcern?.vision ?? '';

                                        setFieldValue(
                                          'areasOfConcern.vision',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            vision: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.vision',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            vision: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="Vision"
                                    defaultlabelid="Vision"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes('VISION') && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern?.vision ?? ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.vision',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            vision: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'HEARING',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedHEARING =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedHEARING,
                                        'HEARING',
                                      );

                                      if (checkedHEARING) {
                                        const value =
                                          values.areasOfConcern?.hearing ?? '';

                                        setFieldValue(
                                          'areasOfConcern.hearing',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            hearing: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.hearing',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            hearing: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="Hearing"
                                    defaultlabelid="Hearing"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes(
                                  'HEARING',
                                ) && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern?.hearing ?? ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.hearing',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            hearing: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'BEHAVIOR',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedBehavior =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedBehavior,
                                        'BEHAVIOR',
                                      );

                                      if (checkedBehavior) {
                                        const value =
                                          values.areasOfConcern?.behavior ?? '';

                                        setFieldValue(
                                          'areasOfConcern.behavior',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            behavior: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.behavior',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            behavior: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="behaviorText"
                                    defaultlabelid="Behavior"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes(
                                  'BEHAVIOR',
                                ) && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern?.behavior ?? ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.behavior',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            behavior: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                              <Grid item xs={9}>
                                <Box className={'flex__ alignItemCenter'}>
                                  <CheckBoxComponent
                                    ischecked={referralReasonsSelected.includes(
                                      'OTHER',
                                    )}
                                    CheckHandleChange={(checkedReason) => {
                                      const checkedOTHER =
                                        checkedReason.target.checked;

                                      updateReferralReasonsChecks(
                                        checkedOTHER,
                                        'OTHER',
                                      );

                                      if (checkedOTHER) {
                                        const value =
                                          values.areasOfConcern?.otherConcern ??
                                          '';

                                        setFieldValue(
                                          'areasOfConcern.otherConcern',
                                          value,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            otherConcern: value,
                                          },
                                        });
                                      } else {
                                        setFieldValue(
                                          'areasOfConcern.otherConcern',
                                          null,
                                        );

                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              {}),
                                            otherConcern: null,
                                          },
                                        });
                                      }
                                    }}
                                    labelid="otherText"
                                    defaultlabelid="Other"
                                    disabled={!isAuthorized}
                                  />
                                </Box>
                                {referralReasonsSelected.includes('OTHER') && (
                                  <Box>
                                    <CommentTextbox
                                      placeholder={rctl.formatMessage({
                                        id: 'myTimeOffReason',
                                        defaultMessage: 'Reason',
                                      })}
                                      maxLength={1000}
                                      Required
                                      rows={2}
                                      Value={
                                        values.areasOfConcern?.otherConcern ??
                                        ''
                                      }
                                      handlechange={(e) => {
                                        setFieldValue(
                                          'areasOfConcern.otherConcern',
                                          e,
                                        );
                                        setLeaReferral({
                                          ...leaReferral,
                                          areasOfConcern: {
                                            ...(leaReferral.areasOfConcern ||
                                              null),
                                            otherConcern: e ?? '',
                                          },
                                        });
                                      }}
                                      disabled={!isAuthorized}
                                    />
                                  </Box>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <MediumTypography
                              className="mainText-md"
                              labelid="lastEvaluationDateText"
                              defaultlabel="Last Evaluation Date"
                            />
                          </Grid>

                          <Grid item className="flex__">
                            <Box className="mr-lg">
                              <DatePickerComponent
                                labelid="lastEvaluationDate"
                                defaultlabelid="Last Evaluation Date"
                                value={
                                  values.lastEvaluationDate
                                    ? dayjs(values.lastEvaluationDate)
                                    : null
                                }
                                required={
                                  values.evaluationFrom === 'Other Assessment'
                                }
                                handlechange={(date: Dayjs | null) => {
                                  const formattedDate =
                                    dayjs(date).format('MM/DD/YYYY');
                                  if (date === null) {
                                    setFieldValue('lastEvaluationDate', null);

                                    setLeaReferral({
                                      ...leaReferral,
                                      lastEvaluationDate: null,
                                    });
                                  } else {
                                    setFieldValue(
                                      'lastEvaluationDate',
                                      formattedDate,
                                    );
                                    setLeaReferral({
                                      ...leaReferral,
                                      lastEvaluationDate: formattedDate,
                                    });
                                  }
                                }}
                                disabledDate={!isAuthorized}
                              />
                              {errors.lastEvaluationDate &&
                                touched.lastEvaluationDate && (
                                  <MediumTypography
                                    label={rctl.formatMessage({
                                      id: errors.lastEvaluationDate,
                                      defaultMessage:
                                        'Last Evaluation Date is required',
                                    })}
                                    className="errorText-md mt-xs"
                                  />
                                )}
                            </Box>
                            <CustomRadioButton
                              className="mt-xs"
                              data={EvaluationQuestions}
                              onSelectItem={(item) => {
                                setFieldValue('evaluationFrom', item);
                                setLeaReferral({
                                  ...leaReferral,
                                  evaluationFrom: item,
                                });
                              }}
                              row={true}
                              value={values.evaluationFrom}
                              disable={!isAuthorized}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid item xs={9}>
                              <Box className={'flex__ alignItemCenter'}>
                                <CheckBoxComponent
                                  ischecked={values.attachBDI2Results ?? false}
                                  CheckHandleChange={(checkedEval) => {
                                    setFieldValue(
                                      'attachBDI2Results',
                                      checkedEval.target.checked,
                                    );
                                    setLeaReferral({
                                      ...leaReferral,
                                      attachBDI2Results:
                                        checkedEval.target.checked,
                                    });
                                  }}
                                  labelid="attachBdi2Text"
                                  defaultlabelid="Attach BDI results"
                                  disabled={!isAuthorized}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={9}>
                              <Box className={'flex__ alignItemCenter'}>
                                <CheckBoxComponent
                                  ischecked={values.attachIFSP ?? false}
                                  CheckHandleChange={(checkedEval) => {
                                    setFieldValue(
                                      'attachIFSP',
                                      checkedEval.target.checked,
                                    );
                                    setLeaReferral({
                                      ...leaReferral,
                                      attachIFSP: checkedEval.target.checked,
                                    });
                                  }}
                                  labelid="attachIFSPText"
                                  defaultlabelid="Attach IFSP"
                                  disabled={!isAuthorized}
                                />
                              </Box>
                            </Grid>
                            <Grid
                              item
                              xs={9}
                              className={'flex__ alignItemCenter'}
                            >
                              <CheckBoxComponent
                                ischecked={values.other ?? false}
                                CheckHandleChange={(checkedEval) => {
                                  const isChecked = checkedEval.target.checked;
                                  setFieldValue('other', isChecked);
                                  if (!isChecked) {
                                    setFieldValue('otherText', '');
                                  }
                                  setLeaReferral((prev) => ({
                                    ...prev,
                                    other: isChecked,
                                    otherText: isChecked ? prev.otherText : '',
                                  }));
                                }}
                                labelid="attachOtherEvalText"
                                defaultlabelid="Other"
                                disabled={!isAuthorized}
                              />
                            </Grid>
                            {values.other && (
                              <Grid item xs={3}>
                                <Textinput
                                  labelid="placeholderDetail"
                                  defaultlabelid="Details"
                                  Required
                                  Value={values.otherText}
                                  inputProps={{
                                    maxLength: 50,
                                  }}
                                  handlechange={(e) => {
                                    setFieldValue('otherText', e);
                                    setLeaReferral({
                                      ...leaReferral,
                                      otherText: e,
                                    });
                                  }}
                                  disabled={!isAuthorized}
                                />
                                {errors.otherText && touched.otherText && (
                                  <MediumTypography
                                    label={rctl.formatMessage({
                                      id: errors.otherText,
                                      defaultMessage:
                                        'Other Details is required',
                                    })}
                                    className="errorText-md"
                                  />
                                )}
                              </Grid>
                            )}
                          </Grid>
                          <Grid
                            item
                            className={'flex__ alignItemCenter'}
                            xs={12}
                          >
                            <MediumTypography
                              className="mainText-md"
                              labelid="isDayCareText"
                              defaultlabel="Is Child is in Day care?"
                            />
                            <CustomRadioButton
                              className="pl-lg"
                              data={DayCareQuestions}
                              onSelectItem={(item) => {
                                setFieldValue(
                                  'childIsInDayCare',
                                  item === 'yes',
                                );
                                setLeaReferral({
                                  ...leaReferral,
                                  childIsInDayCare: item === 'yes',
                                });
                              }}
                              row={true}
                              value={
                                values.childIsInDayCare !== null
                                  ? values.childIsInDayCare
                                    ? 'yes'
                                    : 'no'
                                  : 'no'
                              }
                              disable={!isAuthorized}
                            />
                          </Grid>

                          {values.childIsInDayCare && (
                            <Grid item xs={3}>
                              <Textinput
                                labelid="dayCareNameText"
                                defaultlabelid="Day Care Name"
                                Required
                                Value={values.dayCareName}
                                inputProps={{
                                  maxLength: 50,
                                }}
                                handlechange={(e) => {
                                  setFieldValue('dayCareName', e);
                                  setLeaReferral({
                                    ...leaReferral,
                                    dayCareName: e,
                                  });
                                }}
                                disabled={!isAuthorized}
                              />
                              {errors.dayCareName && touched.dayCareName && (
                                <MediumTypography
                                  label={rctl.formatMessage({
                                    id: errors.dayCareName,
                                    defaultMessage: 'Day Care Name is required',
                                  })}
                                  className="errorText-md"
                                />
                              )}
                            </Grid>
                          )}

                          {values.childIsInDayCare && (
                            <Grid item xs={3}>
                              <Textinput
                                labelid="dayCarePhoneText"
                                defaultlabelid="Day Care Phone Number"
                                Value={values.dayCarePhoneNumber}
                                Required
                                handlechange={(e) => {
                                  const validatedValue = formatPhoneNumber(e);
                                  setFieldValue(
                                    'dayCarePhoneNumber',
                                    validatedValue,
                                  );
                                  setLeaReferral({
                                    ...leaReferral,
                                    dayCarePhoneNumber: validatedValue,
                                  });
                                }}
                                disabled={!isAuthorized}
                              />
                              {errors.dayCarePhoneNumber &&
                                touched.dayCarePhoneNumber && (
                                  <MediumTypography
                                    label={rctl.formatMessage({
                                      id: errors.dayCarePhoneNumber,
                                      defaultMessage:
                                        'Day Care Phone Number is required',
                                    })}
                                    className="errorText-md"
                                  />
                                )}
                            </Grid>
                          )}

                          {values.childIsInDayCare && (
                            <Grid item xs={3}>
                              <Textinput
                                labelid="childScheduleText"
                                defaultlabelid="Child Schedule"
                                Required
                                inputProps={{ maxLength: 50 }}
                                Value={values.childSchedule}
                                handlechange={(e) => {
                                  setFieldValue('childSchedule', e);
                                  setLeaReferral({
                                    ...leaReferral,
                                    childSchedule: e,
                                  });
                                }}
                                disabled={!isAuthorized}
                              />
                              {errors.childSchedule &&
                                touched.childSchedule && (
                                  <MediumTypography
                                    label={rctl.formatMessage({
                                      id: errors.childSchedule,
                                      defaultMessage:
                                        'Child Schedule is required',
                                    })}
                                    className="errorText-md"
                                  />
                                )}
                            </Grid>
                          )}

                          {values.childIsInDayCare && (
                            <Grid item xs={9}>
                              <CommentTextbox
                                placeholder={rctl.formatMessage({
                                  id: 'addressText',
                                  defaultMessage: 'Address',
                                })}
                                Required
                                maxLength={200}
                                Value={values.dayCareAddress}
                                handlechange={(e) => {
                                  setFieldValue('dayCareAddress', e);
                                  setLeaReferral({
                                    ...leaReferral,
                                    dayCareAddress: e,
                                  });
                                }}
                                disabled={!isAuthorized}
                              />
                              {errors.dayCareAddress &&
                                touched.dayCareAddress && (
                                  <MediumTypography
                                    label={rctl.formatMessage({
                                      id: errors.dayCareAddress,
                                      defaultMessage: 'Address is required',
                                    })}
                                    className="errorText-md"
                                  />
                                )}
                            </Grid>
                          )}

                          <Grid item xs={9}>
                            <MediumTypography
                              className="mainText-md mb-sm"
                              labelid="otherReleventInfoText"
                              defaultlabel="Other Relevant Information"
                            />
                            <CommentTextbox
                              placeholder={rctl.formatMessage({
                                id: 'otherReleventPlaceholderText',
                                defaultMessage:
                                  'Other agency involvement or evaluations, living situation, etc.',
                              })}
                              maxLength={2000}
                              Required
                              rows={2}
                              Value={values.otherReleventInfo}
                              handlechange={(e) => {
                                setFieldValue('otherReleventInfo', e);
                                setLeaReferral({
                                  ...leaReferral,
                                  otherReleventInfo: e,
                                });
                              }}
                              disabled={!isAuthorized}
                            />
                            {errors.otherReleventInfo &&
                              touched.otherReleventInfo && (
                                <MediumTypography
                                  label={rctl.formatMessage({
                                    id: errors.otherReleventInfo,
                                    defaultMessage:
                                      'Other Relevant Information is required',
                                  })}
                                  className="errorText-md"
                                />
                              )}
                          </Grid>

                          <Grid item xs={12}>
                            <Box
                              component="div"
                              className="respoScrollTable mb-md"
                            >
                              <MediumTypography
                                className="mainText-md mb-sm"
                                labelid="currentEIServices"
                                defaultlabel="Current EI Services"
                              />
                              <TableContainer>
                                <Table>
                                  <TableHeader
                                    className="listDataTableHead"
                                    headerNames={headCells}
                                    checkBoxRequired={false}
                                  />
                                  <TableBody className="tableRowcss">
                                    {values.earlyInterventionService?.map(
                                      (listData: ServicePlanType, index) => (
                                        <ServiceDeliveryPlanTableRow
                                          key={`${listData.id}-${index + 1}`}
                                          dataLength={
                                            values.earlyInterventionService
                                              ?.length
                                          }
                                          actionRequired={false}
                                          data={listData}
                                          onEdit={() => {}}
                                          onDelete={() => {}}
                                          reasonIfLate={lookUpReasons}
                                          eiService={eiService}
                                          intensity={intensity}
                                          locationOfService={locationOfService}
                                          period={period}
                                          providedBy={providedBy}
                                        />
                                      ),
                                    )}
                                  </TableBody>
                                </Table>
                                {(values.earlyInterventionService === null ||
                                  values.earlyInterventionService.length ===
                                    0) && (
                                  <Box component="div" className="width__100">
                                    <MediumTypography
                                      textstyle={'center'}
                                      labelid="IFSP.emptyMessage"
                                      defaultlabel="No data added yet"
                                      className="p-sm"
                                    />
                                  </Box>
                                )}
                              </TableContainer>
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Grid item xs={9}></Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <Grid item xs={12}>
                              <MediumTypography
                                className="mainText-md mt-lg mb-md"
                                labelid="invitationTpcText"
                                defaultlabel="Invitation to Transition Conference (TC)"
                              />
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              display={'flex'}
                              rowSpacing={'16px'}
                              columnSpacing={2}
                            >
                              <Grid item xs={3} className="mr-lg">
                                <DatePickerComponent
                                  labelid="dateText"
                                  defaultlabelid="Date"
                                  value={
                                    values?.tpc?.date
                                      ? dayjs(values.tpc.date)
                                      : null
                                  }
                                  handlechange={(date: Dayjs | null) => {
                                    const formattedDate =
                                      dayjs(date).format('MM/DD/YYYY');
                                    if (date === null) {
                                      setFieldValue('tpc.date', null);
                                      setLeaReferral({
                                        ...leaReferral,
                                        tpc: {
                                          ...(leaReferral.tpc || null), // handles null safely
                                          date: null,
                                        },
                                      });
                                    } else {
                                      setFieldValue('tpc.date', formattedDate);
                                      setLeaReferral({
                                        ...leaReferral,
                                        tpc: leaReferral.tpc
                                          ? {
                                              ...leaReferral.tpc,
                                              date: formattedDate,
                                            }
                                          : {
                                              date: formattedDate,
                                              time: null,
                                              location: null,
                                            },
                                      });
                                    }
                                  }}
                                  disabledDate={!isAuthorized}
                                />
                                {errors.tpc?.date && touched.tpc?.date && (
                                  <MediumTypography
                                    label={rctl.formatMessage({
                                      id: errors.tpc.date,
                                      defaultMessage: 'Enter valid Date',
                                    })}
                                    className="errorText-md"
                                  />
                                )}
                              </Grid>

                              <Grid item xs={3} className="ml-lg">
                                <AppTimePicker
                                  disable={!values.tpc?.date || !isAuthorized}
                                  sxButtonProps={{
                                    height: '36px',
                                    color: '#2a4241',
                                    fontSize: '14px',
                                    fontFamily: 'Lato-Regular',
                                  }}
                                  minuteStep={15}
                                  value={
                                    values?.tpc?.time
                                      ? getTime(values.tpc.time, '00:00')
                                      : null
                                  }
                                  onTimeChange={(time) => {
                                    if (time === null) {
                                      setFieldValue('tpc.time', null);
                                      setLeaReferral({
                                        ...leaReferral,
                                        tpc: {
                                          ...(leaReferral.tpc || null), // handles null safely
                                          time: null,
                                        },
                                      });
                                    } else {
                                      setFieldValue('tpc.time', time.format());
                                      setLeaReferral({
                                        ...leaReferral,
                                        tpc: leaReferral.tpc
                                          ? {
                                              ...leaReferral.tpc,
                                              time: time.format(),
                                            }
                                          : {
                                              date: null,
                                              time: time.format(),
                                              location: null,
                                            },
                                      });
                                    }
                                  }}
                                />
                                {errors.tpc?.time && touched.tpc?.time && (
                                  <MediumTypography
                                    label={rctl.formatMessage({
                                      id: errors.tpc.time,
                                      defaultMessage: 'Time is required',
                                    })}
                                    className="errorText-md"
                                  />
                                )}
                              </Grid>
                            </Grid>

                            <Grid item xs={6.5}>
                              <Textinput
                                className="mt-md"
                                labelid="locationText"
                                defaultlabelid="Location"
                                Value={values?.tpc?.location ?? ''}
                                inputProps={{
                                  maxLength: 200,
                                }}
                                handlechange={(e) => {
                                  setFieldValue('tpc.location', e);
                                  setLeaReferral({
                                    ...leaReferral,
                                    tpc: leaReferral.tpc
                                      ? {
                                          ...leaReferral.tpc,
                                          location: e,
                                        }
                                      : {
                                          date: null,
                                          time: null,
                                          location: e,
                                        },
                                  });
                                }}
                                disabled={!isAuthorized}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                    <Box>
                      {leaReferral.uiVersion === 1 &&
                      (leaReferral?.parentSignature?.signatureValue !== '' ||
                        leaReferral?.parentSignature?.rowkey !== '')
                        ? renderParentSignatureComponent(values)
                        : renderSignatureComponent()}

                      {lastUpdatedBy && (
                        <Grid item xs={12}>
                          <MediumTypography
                            label={`Last Updated By : ${lastUpdatedBy}`}
                          />
                        </Grid>
                      )}
                    </Box>
                  </>
                );
              }}
            </Formik>
          </Box>

          <Box component="div">
            <Box
              style={{
                pointerEvents:
                  leaReferral?.authorizationStatus === 'Revoked' ||
                  isSignatureInProgress()
                    ? 'none'
                    : 'auto',
                opacity:
                  leaReferral?.authorizationStatus === 'Revoked' ||
                  isSignatureInProgress()
                    ? '0.8'
                    : 'unset',
              }}
            >
              <MediumTypography
                className="mainText-lg pt-md"
                labelid="ConsentForms.refusalTitle"
                defaultlabel="Understanding of Refusal"
              />
              <Grid container rowSpacing={'24px'} columnSpacing={'20px'}>
                <Grid item>
                  <CheckBoxComponent
                    labelid="Consentrefusal.labelText"
                    defaultlabelid="I do not wish to give my permission"
                    ischecked={checked}
                    disabled={isSignatureInProgress() || !isAuthorized}
                    CheckHandleChange={(
                      value: React.ChangeEvent<HTMLInputElement>,
                    ) => onChecked(value.target.checked)}
                  />
                </Grid>
              </Grid>
            </Box>

            {leaReferral.refusal && (
              <Box>
                <DropdownComponent
                  labelid="sspReferral.contact"
                  defaultlabelid="Select Guardian"
                  names={guardianContacts}
                  disabled={
                    leaReferral?.authorizationStatus === 'Revoked' ||
                    !isAuthorized
                  }
                  Required={checkedGuardianRequired()}
                  handleChange={(value) => {
                    if (value === '') {
                      setRevokeGuardian(null);
                      setLeaReferral({
                        ...leaReferral,
                        revokeSignature: null,
                      });
                    } else {
                      const foundContact = guardianContacts.find(
                        (contact) => contact.id === value,
                      );
                      if (foundContact) {
                        const contact = leaReferral?.contact.find(
                          (c) => c.id.toString() === foundContact.id,
                        );
                        if (contact) {
                          setLeaReferral({
                            ...leaReferral,
                            revokeSignature: {
                              name: contact.name,
                              email: contact.email,
                              signature: '',
                              verbal: false,
                              paperConsent: false,
                              partitionKey: '',
                              rowKey: '',
                              isPrimary: contact.isPrimary,
                              relationship: contact.type ?? '',
                              id: contact.id,
                              isLegal: contact.isLegalGuardian,
                              isGuardian: contact.isGuardian,
                            },
                          });
                        }
                        setRevokeGuardian(foundContact);
                      }
                    }
                  }}
                  value={revokeGuardian?.id || ''}
                  className="mt-md width400px"
                />

                <ConsentSignature
                  signatureData={
                    leaReferral?.revokeSignature
                      ? [
                          {
                            ...leaReferral.revokeSignature,
                          },
                        ]
                      : []
                  }
                  minDate={getConsentMinimumDate()}
                  staffSignature={false}
                  revokeChecked={leaReferral.refusal}
                  onSaveClick={onRevokeSignatureSaveClick}
                  verbalConsentValue={[
                    {
                      verbal: leaReferral.revokeSignature?.verbal || false,
                    },
                  ]}
                  paperConsentValue={[
                    {
                      paperConsent:
                        leaReferral.revokeSignature?.paperConsent || false,
                    },
                  ]}
                  onVerbalConsentClick={onRevokeSignatureVerbalConsentClick}
                  onPaperConsentClick={onRevokeSignaturePaperConsentClick}
                  defaultParentGuardianNoId="Guardian Name"
                  parentGuardianNoId="ConsentParentGuardianNoId"
                  relationshipWithChildId="ConsentRelationshipWithChild"
                  defaultRelationshipWithChildId="Guardian Type:"
                  emailId="ConsentEmail"
                  defaultEmailId="Email:"
                  isDateRequired={leaReferral.refusal}
                  viewUpdateId="ConsentViewOrUpdateSignature"
                  defaultViewUpdateId="View/update signature"
                  addSignatureId="ConsentAddSignature"
                  defaultAddSignatureId="Add Signature"
                  verbalConsentId="ConsentVerbalConsent"
                  defaultVerbalConsentId="Verbal Consent"
                  parentSignatureId="ConsentParentSignature"
                  defaultParentSignatureId="Guardian Signature"
                  isPaperConsentRequired={(leaReferral?.uiVersion ?? 1) > 2}
                  isVerbalConsentRequired={(leaReferral?.uiVersion ?? 1) <= 2}
                  paperConsentId="PaperConsent"
                  defaultPaperConsentId="Paper Consent"
                  onConsentSignedDateChange={onRevokeConsentSignDateChange}
                  hasEditPermission={
                    leaReferral?.authorizationStatus === 'Revoked' ||
                    !isAuthorized
                  }
                />
              </Box>
            )}
          </Box>

          <Box component="div">
            <Typography
              dangerouslySetInnerHTML={{ __html: disclaimer }}
              sx={{
                fontFamily: 'Lato-Regular',
                fontSize: '14px',
                fontWeight: 400,
              }}
            />
          </Box>
        </Card>

        <Box
          className="mt-md mb-lg"
          style={{
            display: 'auto',
          }}
        >
          {isAuthorized && (
            <Grid
              container
              direction="row"
              alignItems="right"
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Grid item sx={{ marginRight: '16px' }}>
                <ButtonComponent
                  className="btn-primary btn-cancel"
                  variantType="contained"
                  labelId="Clientpage.cancelbtn"
                  defaultLabelId="Cancel"
                  sxProps={{
                    display:
                      hasEditPermission ||
                      (leaReferral?.authorizationStatus !== 'Revoked' &&
                        leaReferral.refusal)
                        ? 'auto'
                        : 'none',
                  }}
                  onClick={() => {
                    if (
                      JSON.stringify(formRef.current?.initialValues) !==
                      JSON.stringify(formRef.current?.values)
                    ) {
                      setOpenModal(true);
                    } else {
                      navigate(-1);
                    }
                  }}
                />
              </Grid>
              <Grid item>
                <ButtonComponent
                  sxProps={{
                    display:
                      hasEditPermission ||
                      (leaReferral?.authorizationStatus !== 'Revoked' &&
                        leaReferral.refusal)
                        ? 'auto'
                        : 'none',
                  }}
                  className="btn-primary btn-submit"
                  variantType="contained"
                  type="submit"
                  labelId={'Insurance.save'}
                  defaultLabelId="Save"
                  onClick={() => {
                    formRef.current?.submitForm();
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LEAPreschoolForm;
